import React, { useEffect, useState } from 'react'
import styles from './styles.module.css'
import moment from 'moment'
import Button from '../../components/Button'
import { IoIosAdd, IoMdClose } from 'react-icons/io'
import { BiTrash, BiSearch } from 'react-icons/bi'
import { useDispatch, useSelector } from 'react-redux'
import Modal from './Modal'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import Loader from '../../components/Loader'
import { Pagination } from '../../components/Pagination'
import { deleteEquipment, getEquipmentFilters, getEquipmentList } from '../../actions/equipment'

const PAGE_SIZE = 50

export default function Equipment() {
  const dispatch = useDispatch()
  const { list, total, loading, filters } = useSelector(state => state.equipment)

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [mid, setMid] = useState('')
  const [rid, setRid] = useState('')
  const [page, setPage] = useState(0)
  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(false)

  useEffect(() => {
    dispatch(getEquipmentFilters())
  }, [])

  // поиск с задержкой, чтобы не дёргать бэк на каждый символ
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const fetchList = (pageNum = page) => {
    dispatch(getEquipmentList({
      limit: PAGE_SIZE,
      offset: pageNum * PAGE_SIZE,
      mid: mid || undefined,
      rid: rid || undefined,
      search: search || undefined,
    }))
  }

  useEffect(() => {
    fetchList()
  }, [page, mid, rid, search])

  const pageCount = Math.ceil(total / PAGE_SIZE)

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value)
    setPage(0)
  }

  const openModal = (data) => () => {
    setSelectedItem(data || {})
  }

  const closeModal = () => setSelectedItem(null)

  const handleSaved = () => {
    setSelectedItem(null)
    fetchList()
    dispatch(getEquipmentFilters())
  }

  const acceptConfirmation = async () => {
    const id = confirmation.id
    setConfirmation(false)
    const success = await dispatch(deleteEquipment(id))
    if (!success) return
    // если удалили последнюю запись на странице — уходим на предыдущую
    if (list.length === 1 && page > 0) setPage(page - 1)
    else fetchList()
    dispatch(getEquipmentFilters())
  }

  const rejectConfirmation = () => setConfirmation(false)

  const cell = (value) => (value ?? '') === '' ? <span className={styles.dash}>—</span> : value

  const cellDate = (value) => value ? moment(value).format('DD.MM.YYYY') : <span className={styles.dash}>—</span>

  const renderRow = (data) => {
    const { idequipment, inventnumber, serialnumber, equipname, groupname, status, quantity,
      edizm, equiptype, roomname, responsible_fio, incomdate, outcomdate, description } = data
    return (
      <tr key={idequipment} onClick={openModal(data)}>
        <td className={styles.tdNum}>{cell(inventnumber)}</td>
        <td>{cell(serialnumber)}</td>
        <td className={styles.tdName}>{cell(equipname)}</td>
        <td>{cell(groupname)}</td>
        <td>{cell(status)}</td>
        <td>{cell(quantity)}</td>
        <td>{cell(edizm)}</td>
        <td>{cell(equiptype)}</td>
        <td>{cell(roomname)}</td>
        <td>{cell(responsible_fio)}</td>
        <td>{cellDate(incomdate)}</td>
        <td>{cellDate(outcomdate)}</td>
        <td>{cell(description)}</td>
        <td className={styles.tdAction}>
          <button
            type="button"
            className={styles.deleteBtn}
            onClick={(e) => { e.stopPropagation(); setConfirmation({ id: idequipment, name: equipname }) }}
            title="Удалить запись"
            aria-label="Удалить"
          >
            <BiTrash size={22} />
          </button>
        </td>
      </tr>
    )
  }

  return (
    <div className={styles.wrapper}>
      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить «${confirmation.name}»?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
        />
      )}
      {selectedItem &&
        <Modal
          closeModal={closeModal}
          onSaved={handleSaved}
          data={selectedItem}
        />
      }
      <header className={styles.header}>
        <h1>Оборудование и ТМЦ</h1>
      </header>
      <main className={styles.content}>
        <div className={styles.filters}>
          <div className={styles.searchBox}>
            <BiSearch size={18} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Поиск по инв. номеру, серийному номеру, названию"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
            {!!searchInput &&
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => setSearchInput('')}
                title="Очистить"
                aria-label="Очистить поиск"
              >
                <IoMdClose size={18} />
              </button>
            }
          </div>
          <label className={styles.filter}>
            <span>Ответственный:</span>
            <select value={mid} onChange={handleFilterChange(setMid)}>
              <option value="">Все</option>
              {filters.responsible?.map(item =>
                <option key={item.mid} value={item.mid}>{item.fio}</option>
              )}
            </select>
          </label>
          <label className={styles.filter}>
            <span>Помещение:</span>
            <select value={rid} onChange={handleFilterChange(setRid)}>
              <option value="">Все</option>
              {filters.rooms?.map(item =>
                <option key={item.rid} value={item.rid}>{item.name}</option>
              )}
            </select>
          </label>
        </div>

        {loading && !list?.length
          ? <Loader />
          : <Pagination
            pageCount={pageCount}
            pageNumber={page}
            onPageChange={({ selected }) => setPage(selected)}
            loading={loading}
          >
            <Table className={styles.table} maxHeight='calc(100vh - 320px)'>
              <thead>
                <tr>
                  <th>Инв. номер</th>
                  <th>Серийный номер</th>
                  <th className={styles.thName}>Название</th>
                  <th>Группа оборудования</th>
                  <th>Состояние</th>
                  <th>Кол-во</th>
                  <th>Ед.изм.</th>
                  <th>Тип</th>
                  <th>Местонахождение</th>
                  <th>Ответственный</th>
                  <th>Дата оприходования</th>
                  <th>Дата выбытия</th>
                  <th>Примечание</th>
                  <th className={styles.thAction}>Удалить</th>
                </tr>
              </thead>
              <tbody>
                {list?.length
                  ? list.map(renderRow)
                  : <tr><td className={styles.emptyRow} colSpan={14}>Нет записей</td></tr>}
              </tbody>
            </Table>
          </Pagination>
        }
        <div className={styles.actions}>
          <Button style={{ padding: '.5rem 1.5rem' }} onClick={openModal()}>
            <IoIosAdd size={30} />
            Добавить
          </Button>
          <span className={styles.counter}>Всего: {total}</span>
        </div>
      </main>
    </div>
  )
}
